import type { OiBbox } from '@/lib/api/oiSearch';
import type { GeoJsonPolygon } from '@/lib/api/types';
import type { Feature, FeatureCollection, LineString, Point, Polygon } from 'geojson';

export type LngLat = [number, number];
export type CornerHandle = 'nw' | 'ne' | 'se' | 'sw';
export type SideHandle = 'n' | 'e' | 's' | 'w';
export type HandleId = CornerHandle | SideHandle;

/** spec D6 — FlightBlender 400s bboxes with a diagonal > 450 km; a 300 km
 *  square is ≈ 424 km corner to corner. */
export const SEARCH_SIZE_KM = { min: 1, max: 300, default: 25 } as const;

const KM_PER_DEG = 111.32;
const EARTH_RADIUS_KM = 6371;
/** Side bars span this fraction of the side they sit on. */
const SIDE_BAR_FRACTION = 0.2;

function kmPerDegLng(lat: number): number {
  return KM_PER_DEG * Math.max(0.01, Math.cos((lat * Math.PI) / 180));
}

export function clampSizeKm(km: number): number {
  if (!Number.isFinite(km)) return SEARCH_SIZE_KM.default;
  return Math.min(SEARCH_SIZE_KM.max, Math.max(SEARCH_SIZE_KM.min, Math.round(km)));
}

export function squareBbox([lng, lat]: LngLat, sideKm: number): OiBbox {
  const half = sideKm / 2;
  const dLat = half / KM_PER_DEG;
  const dLng = half / kmPerDegLng(lat);
  return { swLat: lat - dLat, swLng: lng - dLng, neLat: lat + dLat, neLng: lng + dLng };
}

export function areaPolygon(center: LngLat, sideKm: number): GeoJsonPolygon {
  const b = squareBbox(center, sideKm);
  return {
    type: 'Polygon',
    coordinates: [
      [
        [b.swLng, b.swLat],
        [b.neLng, b.swLat],
        [b.neLng, b.neLat],
        [b.swLng, b.neLat],
        [b.swLng, b.swLat],
      ],
    ],
  };
}

export function handlePoints(
  center: LngLat,
  sideKm: number,
): { corners: Record<CornerHandle, LngLat>; sides: Record<SideHandle, LngLat> } {
  const b = squareBbox(center, sideKm);
  const [lng, lat] = center;
  return {
    corners: {
      nw: [b.swLng, b.neLat],
      ne: [b.neLng, b.neLat],
      se: [b.neLng, b.swLat],
      sw: [b.swLng, b.swLat],
    },
    sides: {
      n: [lng, b.neLat],
      e: [b.neLng, lat],
      s: [lng, b.swLat],
      w: [b.swLng, lat],
    },
  };
}

/** New side length after dragging `handle` to `pointer`. The square stays
 *  centred, so the side is twice the pointer's distance from the centre along
 *  the handle's axis (corners take the larger axis). */
export function sizeFromHandleDrag(center: LngLat, pointer: LngLat, handle: HandleId): number {
  const [lng, lat] = center;
  const dxKm = Math.abs(pointer[0] - lng) * kmPerDegLng(lat);
  const dyKm = Math.abs(pointer[1] - lat) * KM_PER_DEG;
  switch (handle) {
    case 'n':
    case 's':
      return clampSizeKm(2 * dyKm);
    case 'e':
    case 'w':
      return clampSizeKm(2 * dxKm);
    case 'nw':
    case 'ne':
    case 'se':
    case 'sw':
      return clampSizeKm(2 * Math.max(dxKm, dyKm));
    default: {
      const _exhaustive: never = handle;
      return _exhaustive;
    }
  }
}

type AreaProps = { role: 'area' | 'corner' | 'side'; handle: HandleId | null };

export function buildSearchAreaFC(
  center: LngLat,
  sideKm: number,
): FeatureCollection<Polygon | Point | LineString, AreaProps> {
  const b = squareBbox(center, sideKm);
  const { corners, sides } = handlePoints(center, sideKm);
  const halfBarLng = ((b.neLng - b.swLng) * SIDE_BAR_FRACTION) / 2;
  const halfBarLat = ((b.neLat - b.swLat) * SIDE_BAR_FRACTION) / 2;

  const area: Feature<Polygon, AreaProps> = {
    type: 'Feature',
    geometry: areaPolygon(center, sideKm),
    properties: { role: 'area', handle: null },
  };
  const cornerFeatures = (Object.keys(corners) as CornerHandle[]).map(
    (h): Feature<Point, AreaProps> => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: corners[h] },
      properties: { role: 'corner', handle: h },
    }),
  );
  const sideFeatures = (Object.keys(sides) as SideHandle[]).map(
    (h): Feature<LineString, AreaProps> => {
      const [x, y] = sides[h];
      const horizontal = h === 'n' || h === 's';
      const coordinates: LngLat[] = horizontal
        ? [
            [x - halfBarLng, y],
            [x + halfBarLng, y],
          ]
        : [
            [x, y - halfBarLat],
            [x, y + halfBarLat],
          ];
      return {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates },
        properties: { role: 'side', handle: h },
      };
    },
  );
  return { type: 'FeatureCollection', features: [area, ...cornerFeatures, ...sideFeatures] };
}

/** Rounded to 4 dp (~11 m) so a stable area yields a stable query key. */
export function bboxKey(b: OiBbox): string {
  return [b.swLat, b.swLng, b.neLat, b.neLng].map((n) => n.toFixed(4)).join(',');
}

export function haversineKm([aLng, aLat]: LngLat, [bLng, bLat]: LngLat): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}
